import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MapPin, Clock, Phone, PlayCircle, Loader2, Calendar, DollarSign, CheckCircle2, Hammer, RotateCcw, Sun } from "lucide-react";

const BUCKETS = [
  { key: "continue", label: "Continue Visit", statuses: ["field_visit"], icon: PlayCircle, testid: "bucket-continue" },
  { key: "waiting", label: "Waiting for Office", statuses: ["office_review", "scope_preparing", "estimating"], icon: Clock, testid: "bucket-waiting" },
  { key: "price", label: "Price Ready", statuses: ["price_ready"], icon: DollarSign, testid: "bucket-price" },
  { key: "presented", label: "Estimate Presented", statuses: ["estimate_presented"], icon: DollarSign, testid: "bucket-presented" },
  { key: "followup", label: "Follow-Up", statuses: ["follow_up"], icon: RotateCcw, testid: "bucket-followup" },
  { key: "active", label: "Active Projects", statuses: ["sold", "active_project"], icon: Hammer, testid: "bucket-active" },
  { key: "completed", label: "Completed", statuses: ["completed"], icon: CheckCircle2, testid: "bucket-completed" },
];

export default function SalesDashboard() {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);

  const load = async () => {
    try {
      const [a, p] = await Promise.all([api.get("/appointments"), api.get("/projects")]);
      setAppointments(a.data);
      setProjects(p.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startVisit = async (appt) => {
    setStarting(appt.id);
    try {
      const { data } = await api.post("/projects/start", { appointment_id: appt.id });
      navigate(`/visit/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setStarting(null);
    }
  };

  const openProject = (p) => {
    if (["sold", "active_project", "completed", "price_ready", "estimate_presented", "follow_up", "lost"].includes(p.status)) navigate(`/project/${p.id}`);
    else navigate(`/visit/${p.id}`);
  };

  const greeting = () => {
    const h = new Date().getHours();
    return h < 12 ? "Good Morning" : h < 18 ? "Good Afternoon" : "Good Evening";
  };

  if (loading) {
    return <AppShell><div className="flex justify-center py-32"><Loader2 className="w-8 h-8 text-orange-500 animate-spin" /></div></AppShell>;
  }

  const noProjectAppts = appointments.filter((a) => !a.project_id);

  return (
    <AppShell title="Field Dashboard">
      <div className="p-4 sm:p-6 pb-24">
        <div className="flex items-center gap-3 mb-1">
          <Sun className="w-6 h-6 text-orange-400" />
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="dashboard-greeting">
            {greeting()}
          </h1>
        </div>
        <p className="text-slate-500 mb-8">Here's what's on your plate today.</p>

        {/* Today's appointments */}
        <section className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-orange-400" />
            <h2 className="font-display text-2xl font-bold">Today's Appointments</h2>
            <span className="text-sm text-slate-500">({noProjectAppts.length})</span>
          </div>
          {noProjectAppts.length === 0 ? (
            <div className="text-slate-500 text-sm bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
              No new appointments. Great job staying on top of things!
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {noProjectAppts.map((a, i) => (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  data-testid={`appointment-card-${a.id}`}
                  className="rounded-2xl bg-slate-900 border border-slate-800 p-5 hover:border-orange-500/40 transition-colors shadow-xl"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-display text-xl font-bold">{a.customer_name}</div>
                      <div className="text-orange-400 text-sm font-medium">{a.project_type}</div>
                    </div>
                    <span className="text-xs px-2 py-1 rounded-full bg-slate-800 text-slate-400 font-mono-data">
                      {new Date(a.time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                    </span>
                  </div>
                  <div className="mt-3 space-y-1.5 text-sm text-slate-400">
                    <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-600" /> {a.address}</div>
                    <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-600" /> {a.phone}</div>
                  </div>
                  <Button
                    data-testid={`start-visit-button-${a.id}`}
                    onClick={() => startVisit(a)}
                    disabled={starting === a.id}
                    className="w-full mt-4 h-12 bg-orange-500 hover:bg-orange-600 text-slate-950 font-bold text-base hover:scale-[1.02] transition-transform shadow-lg shadow-orange-500/20"
                  >
                    {starting === a.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <><PlayCircle className="w-5 h-5 mr-2" /> START VISIT</>}
                  </Button>
                </motion.div>
              ))}
            </div>
          )}
        </section>

        {/* Project buckets */}
        {BUCKETS.map((bucket) => {
          const items = projects.filter((p) => bucket.statuses.includes(p.status));
          if (items.length === 0) return null;
          const Icon = bucket.icon;
          return (
            <section key={bucket.key} className="mb-8" data-testid={bucket.testid}>
              <div className="flex items-center gap-2 mb-4">
                <Icon className="w-5 h-5 text-slate-400" />
                <h2 className="font-display text-xl font-bold">{bucket.label}</h2>
                <span className="text-sm text-slate-500">({items.length})</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {items.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`project-card-${p.id}`}
                    onClick={() => openProject(p)}
                    className="text-left rounded-2xl bg-slate-900 border border-slate-800 p-4 hover:border-orange-500/40 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-display text-lg font-bold">{p.customer_name}</div>
                      <StatusBadge status={p.status} />
                    </div>
                    <div className="text-sm text-slate-500 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {p.address}</div>
                    {p.status === "price_ready" && p.estimate && (
                      <div className="mt-3 flex items-center gap-2 text-emerald-400 font-bold text-lg font-mono-data">
                        <DollarSign className="w-5 h-5" /> {p.estimate.amount.toLocaleString()}
                        <span className="text-xs font-normal text-emerald-300/70 ml-1">PRICE READY</span>
                      </div>
                    )}
                    {p.completion_pct != null && ["active_project", "sold"].includes(p.status) && (
                      <div className="mt-3">
                        <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                          <div className="h-full bg-blue-500" style={{ width: `${p.completion_pct || 0}%` }} />
                        </div>
                        <div className="text-xs text-slate-500 mt-1">{p.completion_pct || 0}% complete</div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </AppShell>
  );
}
